import { useCallback, useLayoutEffect, useState, RefObject } from 'react';

export type AnchoredStyle = {
  position: 'fixed';
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
};

type Options = {
  // Pixel gap between the anchor and the panel.
  gap?: number;
  // Hard cap on the panel height; the real cap is the smaller of this and
  // the space available in the chosen direction.
  maxHeight?: number;
  // Floor for the panel width (it otherwise matches the anchor's width).
  minWidth?: number;
};

// Positions a portaled dropdown panel directly under (or, when there isn't
// room, over) an anchor element using FIXED viewport coordinates. Because
// the panel is portaled to <html> and positioned with `position: fixed`, it
// escapes every clipping / scrolling ancestor — so a dropdown opened inside
// a modal (whose body is `overflow-y-auto`) is no longer cropped.
//
// Coordinates come from getBoundingClientRect(), which already accounts for
// any CSS `zoom` applied to <body> for the Appearance preference, so placing
// the panel in <html> (outside the zoomed <body>, like the Modal does) keeps
// it aligned at every zoom level.
//
// Recomputes on open, on resize, and on scroll (capture phase, so scrolling
// any ancestor container — e.g. the modal body — keeps the panel attached).
export function useAnchoredDropdown(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  { gap = 4, maxHeight = 256, minWidth = 0 }: Options = {},
): AnchoredStyle | null {
  const [style, setStyle] = useState<AnchoredStyle | null>(null);

  const reposition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // Flip up only when below is genuinely cramped and above has more room.
    const flipUp = spaceBelow < Math.min(maxHeight, 240) && spaceAbove > spaceBelow;
    const avail = (flipUp ? spaceAbove : spaceBelow) - gap - 8;
    const mh = Math.max(96, Math.min(maxHeight, avail));
    const width = Math.max(r.width, minWidth);
    // Keep the panel within the viewport's horizontal bounds.
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    if (flipUp) {
      setStyle({ position: 'fixed', left, width, bottom: window.innerHeight - r.top + gap, maxHeight: mh });
    } else {
      setStyle({ position: 'fixed', left, width, top: r.bottom + gap, maxHeight: mh });
    }
  }, [anchorRef, gap, maxHeight, minWidth]);

  useLayoutEffect(() => {
    if (!open) { setStyle(null); return; }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  return style;
}
